/**
 * Content Ownership lookup API client (CCPL-8360).
 * Admin Explorer requests never send `mode`; CCPL-8372 Dashboard requests
 * use an explicit, fixed one-level browse contract.
 */

import apiService from '../../utils/apiService';
import { CONTENT_OWNERSHIP_API } from '../../../Constants';
import {
  isDashboardOwnershipPathEligible,
  normalizeDashboardOwnershipPath
} from '../../Dashboard/dashboardOwnershipUtils';

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 50;
const DASHBOARD_OWNERSHIP_PAGE_SIZE = 100;
const MAX_DASHBOARD_OWNERSHIP_PAGES = 200;
const DASHBOARD_OWNERSHIP_BATCH_SIZE = 500;
const CONTENT_OWNERSHIP_BATCH_API = `${CONTENT_OWNERSHIP_API}/batch`;

/**
 * Build query params for GET admin/content-ownership.
 * Omits empty optional fields and never includes `mode`.
 */
export function buildOwnershipQuery(params = {}) {
  const query = {};

  if (Object.prototype.hasOwnProperty.call(params, 'mode')) {
    // Explicitly ignore — Admin explorer must not send mode
  }

  const path = typeof params.path === 'string' ? params.path.trim() : '';
  if (path) {
    query.path = path.replace(/^\/+|\/+$/g, '');
  }

  if (params.level !== undefined && params.level !== null && params.level !== '') {
    query.level = Number(params.level);
  }

  if (params.type && params.type !== 'all') {
    query.type = params.type;
  } else if (params.type === 'all') {
    query.type = 'all';
  }

  if (params.type === 'folder') {
    query.extension = 'all';
  } else if (Array.isArray(params.extension)) {
    const selected = params.extension
      .map((token) => String(token).replace(/^\./, '').trim().toLowerCase())
      .filter(Boolean);
    query.extension = selected.length ? selected.join(',') : 'all';
  } else   if (params.extension) {
    query.extension = String(params.extension).replace(/^\./, '').toLowerCase();
  }

  query.page = Number(params.page) > 0 ? Number(params.page) : DEFAULT_PAGE;
  query.limit = Number(params.limit) > 0 ? Number(params.limit) : DEFAULT_LIMIT;

  return query;
}

export function displayOwner(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return String(value);
}

export function buildDashboardBrowseOwnershipQuery(params = {}) {
  const path = normalizeDashboardOwnershipPath(params.path);
  if (!isDashboardOwnershipPathEligible(path)) {
    return {};
  }

  return {
    path,
    mode: 'browse',
    level: 1,
    type: 'all',
    extension: 'all',
    includeUnattributed: true,
    page: Number(params.page) > 0 ? Number(params.page) : DEFAULT_PAGE,
    limit: DASHBOARD_OWNERSHIP_PAGE_SIZE
  };
}

/**
 * Map Axios/apiService response to a stable result for the panel.
 * apiService returns error.response on HTTP errors (does not throw for 4xx/5xx).
 */
export function mapOwnershipResponse(response) {
  const status = response?.status ?? response?.data?.status_code ?? 0;
  const body = response?.data ?? {};
  const message =
    typeof body.message === 'string' && body.message.trim()
      ? body.message.trim()
      : 'Request failed';

  if (status === 200 && body.status_code === 200) {
    const data = body.data || {};
    return {
      ok: true,
      status: 200,
      data: {
        enabled: data.enabled !== false,
        path: data.path || '',
        level: data.level,
        items: Array.isArray(data.items) ? data.items : [],
        pagination: {
          currentPage: data.pagination?.currentPage || 1,
          pageSize: data.pagination?.pageSize || DEFAULT_LIMIT,
          totalRecords: data.pagination?.totalRecords || 0,
          totalPages: data.pagination?.totalPages || 0,
          hasNextPage: Boolean(data.pagination?.hasNextPage),
          hasPrevPage: Boolean(data.pagination?.hasPrevPage)
        },
        truncated: Boolean(data.truncated),
        infoNote: typeof data.infoNote === 'string' ? data.infoNote : '',
        emptyMessage: typeof data.emptyMessage === 'string' ? data.emptyMessage : '',
        truncatedMessage:
          typeof data.truncatedMessage === 'string'
            ? data.truncatedMessage
            : typeof body.message === 'string' && data.truncated
              ? body.message
              : ''
      }
    };
  }

  if (status === 503 || body.status_code === 503) {
    return {
      ok: false,
      status: 503,
      code: 'FEATURE_DISABLED',
      message: message || 'Content ownership lookup is unavailable.'
    };
  }

  if (status === 403 || body.status_code === 403) {
    return {
      ok: false,
      status: 403,
      code: 'FORBIDDEN',
      message: message || 'You are not permitted to perform this action.'
    };
  }

  if (status === 404 || body.status_code === 404) {
    return {
      ok: false,
      status: 404,
      code: 'NOT_FOUND',
      message: message || 'Path not found'
    };
  }

  if (status === 400 || body.status_code === 400) {
    return {
      ok: false,
      status: 400,
      code: 'VALIDATION_FAILED',
      message
    };
  }

  if (status === 429 || body.status_code === 429) {
    return {
      ok: false,
      status: 429,
      code: 'RATE_LIMITED',
      message
    };
  }

  return {
    ok: false,
    status: status || 500,
    code: 'ERROR',
    message: status ? message : 'Unable to reach the ownership service.'
  };
}

async function requestOwnershipQuery(query) {
  try {
    const response = await apiService.get(CONTENT_OWNERSHIP_API, { params: query });
    if (!response) {
      return {
        ok: false,
        status: 500,
        code: 'ERROR',
        message: 'Unable to reach the ownership service.'
      };
    }
    return mapOwnershipResponse(response);
  } catch (error) {
    const errMessage =
      error && typeof error.message === 'string' ? error.message : 'Unable to reach the ownership service.';
    return {
      ok: false,
      status: 500,
      code: 'ERROR',
      message: errMessage
    };
  }
}

export async function fetchOwnership(params = {}) {
  const query = buildOwnershipQuery(params);
  if (!query.path) {
    return {
      ok: false,
      status: 400,
      code: 'VALIDATION_FAILED',
      message: 'Path is required.'
    };
  }

  if (Object.prototype.hasOwnProperty.call(query, 'mode')) {
    delete query.mode;
  }

  return requestOwnershipQuery(query);
}

export async function fetchDashboardOwnershipPage(params = {}) {
  const query = buildDashboardBrowseOwnershipQuery(params);
  if (!query.path) {
    return {
      ok: false,
      status: 400,
      code: 'VALIDATION_FAILED',
      message: 'An eligible Dashboard path is required.'
    };
  }

  return requestOwnershipQuery(query);
}

export async function fetchDashboardOwnershipBatch({
  path,
  items
} = {}) {
  const normalizedPath = normalizeDashboardOwnershipPath(path);
  if (
    !isDashboardOwnershipPathEligible(normalizedPath)
    || !Array.isArray(items)
    || items.length < 1
    || items.length > DASHBOARD_OWNERSHIP_BATCH_SIZE
  ) {
    return {
      ok: false,
      status: 400,
      code: 'VALIDATION_FAILED',
      message: 'An eligible Dashboard path and ownership batch are required.'
    };
  }

  try {
    const response = await apiService.post(CONTENT_OWNERSHIP_BATCH_API, {
      path: normalizedPath,
      items
    });
    return mapOwnershipResponse(response);
  } catch (error) {
    return {
      ok: false,
      status: 500,
      code: 'ERROR',
      message:
        error && typeof error.message === 'string'
          ? error.message
          : 'Unable to reach the ownership service.'
    };
  }
}

const cancelledOwnershipResult = (items) => ({
  ok: false,
  status: 0,
  code: 'CANCELLED',
  message: 'Ownership request is no longer current.',
  partialItems: items
});

export async function fetchAllDashboardOwnershipBatches({
  path,
  items = [],
  shouldContinue = () => true,
  onBatch
} = {}) {
  if (!Array.isArray(items) || items.length === 0) {
    return {
      ok: true,
      status: 200,
      data: { items: [], truncated: false }
    };
  }

  const collectedItems = [];
  let truncated = false;

  for (let offset = 0; offset < items.length; offset += DASHBOARD_OWNERSHIP_BATCH_SIZE) {
    if (!shouldContinue()) {
      return cancelledOwnershipResult(collectedItems);
    }

    const result = await fetchDashboardOwnershipBatch({
      path,
      items: items.slice(offset, offset + DASHBOARD_OWNERSHIP_BATCH_SIZE)
    });
    if (!shouldContinue()) {
      return cancelledOwnershipResult(collectedItems);
    }
    if (!result.ok) {
      return {
        ...result,
        partialItems: collectedItems
      };
    }

    const batchItems = Array.isArray(result.data?.items) ? result.data.items : [];
    collectedItems.push(...batchItems);
    truncated = truncated || Boolean(result.data?.truncated);
    if (typeof onBatch === 'function') {
      onBatch(batchItems, result.data);
    }
    if (truncated) {
      break;
    }
  }

  return {
    ok: true,
    status: 200,
    data: {
      items: collectedItems,
      truncated
    }
  };
}

export async function fetchAllDashboardOwnership({
  path,
  shouldContinue = () => true,
  onPage
} = {}) {
  const collectedItems = [];
  let page = DEFAULT_PAGE;
  let latestData = null;

  while (page <= MAX_DASHBOARD_OWNERSHIP_PAGES) {
    if (!shouldContinue()) {
      return cancelledOwnershipResult(collectedItems);
    }

    const result = await fetchDashboardOwnershipPage({ path, page });
    if (!shouldContinue()) {
      return cancelledOwnershipResult(collectedItems);
    }
    if (!result.ok) {
      return {
        ...result,
        partialItems: collectedItems
      };
    }

    latestData = result.data;
    const pageItems = Array.isArray(result.data.items) ? result.data.items : [];
    collectedItems.push(...pageItems);
    if (typeof onPage === 'function') {
      onPage(pageItems, result.data);
    }

    if (result.data.truncated || !result.data.pagination.hasNextPage) {
      return {
        ok: true,
        status: 200,
        data: {
          ...result.data,
          items: collectedItems
        }
      };
    }

    const totalPages = Number(result.data.pagination.totalPages) || 0;
    if (totalPages > MAX_DASHBOARD_OWNERSHIP_PAGES) {
      return {
        ok: false,
        status: 422,
        code: 'RESULT_LIMIT_EXCEEDED',
        message: 'Ownership result exceeds the supported page limit.',
        partialItems: collectedItems
      };
    }
    page += 1;
  }

  return {
    ok: false,
    status: 422,
    code: 'RESULT_LIMIT_EXCEEDED',
    message: 'Ownership result exceeds the supported page limit.',
    partialItems: collectedItems,
    data: latestData
  };
}

export {
  DEFAULT_PAGE,
  DEFAULT_LIMIT,
  CONTENT_OWNERSHIP_API,
  DASHBOARD_OWNERSHIP_PAGE_SIZE,
  DASHBOARD_OWNERSHIP_BATCH_SIZE,
  CONTENT_OWNERSHIP_BATCH_API,
  MAX_DASHBOARD_OWNERSHIP_PAGES
};
